import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { 
  Monitor, 
  Mail, 
  Smartphone, 
  FileText, 
  Calendar, 
  Video,
  TrendingUp
} from "lucide-react";

const ServicesSection = () => {
  const services = [
    {
      icon: Monitor,
      title: "Desktop Banner Ads",
      description: "High-impact display advertising across our desktop platform"
    },
    {
      icon: Mail,
      title: "Email Lists", 
      description: "Direct access to our engaged mining industry email database"
    },
    {
      icon: Smartphone,
      title: "Mobile Web Ads",
      description: "Optimized mobile advertising for on-the-go professionals"
    },
    {
      icon: FileText,
      title: "Sponsored Content Article",
      description: "Editorial-style content that engages while promoting your message"
    },
    {
      icon: Calendar,
      title: "Event Sponsorships",
      description: "Partner with us at major mining conferences and trade shows"
    },
    {
      icon: Video,
      title: "Video Coverage",
      description: "Premium video content and brand amplification opportunities"
    }
  ];

  const managedServices = [
    {
      category: "Social Media",
      items: ["Content Strategy", "Community Management", "Paid Social Campaigns"]
    },
    {
      category: "Web Development", 
      items: ["Responsive Design", "SEO Optimization", "Analytics Integration"]
    },
    {
      category: "Content Advertising",
      items: ["Editorial Calendar", "Video Production", "Thought Leadership"]
    }
  ];

  return (
    <section 
      id="services" 
      className="py-20 relative"
      style={{
        backgroundImage: 'linear-gradient(rgba(0,0,0,0.8), rgba(0,0,0,0.8)), url("https://customer-assets.emergentagent.com/job_xd-project-landing/artifacts/1gk62qus_Services-Background.jpg")',
        backgroundSize: 'cover',
        backgroundPosition: 'center center',
        backgroundRepeat: 'no-repeat',
        backgroundAttachment: 'scroll'
      }}
    >
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-yellow-500 mb-4">
            KITCO REACH
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Our comprehensive advertising services help mining companies build their brand and generate leads
          </p>
        </div>

        {/* Our Services Grid */}
        <div className="mb-16">
          <h3 className="text-3xl font-bold text-white mb-8 text-center">Our Services</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => {
              const IconComponent = service.icon;
              return (
                <Card key={index} className="bg-gray-800 border-yellow-500/20 hover:border-yellow-500/60 hover:shadow-[0_0_20px_rgba(244,185,66,0.3)] hover:bg-gray-800/80 transition-all duration-300 group hover:-translate-y-1 hover:scale-[1.02] relative overflow-hidden before:absolute before:inset-0 before:bg-gradient-to-r before:from-yellow-500/10 before:to-transparent before:opacity-0 hover:before:opacity-100 before:transition-opacity before:duration-300">
                  <CardHeader className="text-center pb-4">
                    <IconComponent className="w-12 h-12 text-yellow-500 mx-auto mb-4 group-hover:scale-110 transition-transform" />
                    <CardTitle className="text-white text-lg">{service.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-300 text-sm text-center">{service.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Investment Trends */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
          <div>
            <h3 className="text-2xl font-bold text-yellow-500 mb-6">Investment Trends</h3>
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <TrendingUp className="w-5 h-5 text-yellow-500" />
                <span className="text-white">Mining Tours</span>
              </div>
              <div className="flex items-center gap-3">
                <FileText className="w-5 h-5 text-yellow-500" />
                <span className="text-white">Latest Press Releases</span>
              </div>
              <div className="flex items-center gap-3">
                <Video className="w-5 h-5 text-yellow-500" />
                <span className="text-white">Podcasts</span>
              </div>
            </div>
          </div>
          
          <div>
            <h3 className="text-2xl font-bold text-yellow-500 mb-6">Fully Managed Services</h3>
            <div className="space-y-4">
              {managedServices.map((service, index) => (
                <div key={index}>
                  <h4 className="text-white font-semibold mb-2">{service.category}</h4>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {service.items.map((item, i) => (
                      <Badge key={i} variant="outline" className="border-yellow-500/40 text-yellow-500">
                        {item}
                      </Badge>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center">
          <Button 
            size="lg" 
            className="bg-yellow-500 hover:bg-yellow-400 text-black font-semibold px-12 py-4 text-lg"
            onClick={() => {
              document.getElementById('contact')?.scrollIntoView({ 
                behavior: 'smooth' 
              });
            }}
          >
            FIND OUT MORE
          </Button>
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;